import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { ApiError, type Group } from "../api.ts";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { FileBadge, FolderBadge } from "../components/FileBadge.tsx";
import filePage from "../components/file/FilePage.module.css";
import styles from "../components/groups/Groups.module.css";
import { Icon } from "../components/Icon.tsx";
import { EmailAddForm } from "../components/people/EmailAddForm.tsx";
import { PersonRow } from "../components/people/PersonRow.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { formatCount, formatDateTime } from "../lib/format.ts";
import {
  groupQuery,
  keys,
  meQuery,
  messageOf,
  queryClient,
  useDeleteGroup,
  usePatchGroup,
} from "../lib/queries.ts";

export const Route = createFileRoute("/groups/$id")({
  loader: ({ context: { queryClient }, params: { id } }) =>
    queryClient.ensureQueryData(groupQuery(id)),
  component: GroupPage,
  pendingComponent: GroupPending,
  errorComponent: GroupError,
});

function GroupPage() {
  const { id } = Route.useParams();
  const { data: group } = useSuspenseQuery(groupQuery(id));

  useEffect(() => {
    document.title = `${group.name} · Vault`;
  }, [group.name]);

  return (
    <div className={filePage.page}>
      <BackLink />
      <GroupHeader group={group} />
      <div className={filePage.panels}>
        <MembersPanel group={group} />
        <SharedPanel group={group} />
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/groups" className={`${ui.button} ${filePage.back}`} data-variant="frame">
      <Icon name="back" />
      All groups
    </Link>
  );
}

function GroupHeader({ group }: { group: Group }) {
  const [renaming, setRenaming] = useState(false);

  return (
    <header className={filePage.header}>
      <span className={styles.sticker} aria-hidden="true">
        <Icon name="people" />
      </span>
      <div className={filePage.identity}>
        {renaming ? (
          <RenameForm group={group} onDone={() => setRenaming(false)} />
        ) : (
          <div className={filePage.titleRow}>
            <h1 className={filePage.title}>{group.name}</h1>
            <button
              type="button"
              className={`${ui.iconButton} ${filePage.rename}`}
              data-variant="ghost"
              onClick={() => setRenaming(true)}
              aria-label={`Rename ${group.name}`}
              title="Rename"
            >
              <Icon name="pencil" />
            </button>
          </div>
        )}
        <ul className={filePage.meta}>
          <li>{formatCount(group.members.length, "person", "people")}</li>
          <li>
            {group.usedBy.length > 0
              ? `On ${formatCount(group.usedBy.length, "file", "files")}`
              : "Not on any file"}
          </li>
          <li>
            Created <time dateTime={group.createdAt}>{formatDateTime(group.createdAt)}</time>
          </li>
        </ul>
      </div>
      <div className={`${filePage.headerActions} ${styles.headerActions}`}>
        <DeleteButton group={group} />
      </div>
    </header>
  );
}

function RenameForm({ group, onDone }: { group: Group; onDone: () => void }) {
  const patch = usePatchGroup(group.id);
  const [name, setName] = useState(group.name);
  const trimmed = name.trim();
  const error = patch.error ? messageOf(patch.error) : null;

  return (
    <form
      className={filePage.renameForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed === "" || trimmed === group.name) return onDone();
        patch.mutate({ name: trimmed }, { onSuccess: onDone });
      }}
    >
      <label className={ui.visuallyHidden} htmlFor="group-rename">
        Group name
      </label>
      <input
        id="group-rename"
        className={`${ui.input} ${filePage.renameInput}`}
        value={name}
        maxLength={80}
        required
        autoComplete="off"
        // biome-ignore lint/a11y/noAutofocus: the field replaces the title the user just chose to edit
        autoFocus
        aria-invalid={error ? true : undefined}
        onChange={(event) => setName(event.currentTarget.value)}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === "Escape") onDone();
        }}
      />
      <div className={filePage.renameActions}>
        <button
          type="button"
          className={ui.button}
          data-variant="frame"
          onClick={onDone}
          disabled={patch.isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={ui.button}
          data-variant="primary"
          disabled={patch.isPending || trimmed === ""}
        >
          {patch.isPending ? "Saving…" : "Save"}
        </button>
      </div>
      {error ? (
        <p className={filePage.frameError} role="alert">
          <Icon name="alert" />
          {error}
        </p>
      ) : null}
    </form>
  );
}

function DeleteButton({ group }: { group: Group }) {
  const [open, setOpen] = useState(false);
  const remove = useDeleteGroup(group.id);
  const navigate = useNavigate();
  const inUse = group.usedBy.length > 0;

  return (
    <>
      <button
        type="button"
        className={`${ui.button} ${filePage.deleteAction}`}
        onClick={() => setOpen(true)}
      >
        <Icon name="trash" />
        Delete
      </button>
      <ConfirmDialog
        open={open}
        title={inUse ? `${group.name} is still sharing files` : `Delete ${group.name}?`}
        confirmLabel={remove.isPending ? "Deleting…" : "Delete group"}
        cancelLabel={inUse ? "Close" : "Cancel"}
        pending={remove.isPending}
        confirmDisabled={inUse}
        error={remove.error ? messageOf(remove.error) : null}
        onCancel={() => {
          setOpen(false);
          remove.reset();
        }}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: async () => {
              setOpen(false);
              await navigate({ to: "/groups" });
              queryClient.removeQueries({ queryKey: keys.group(group.id) });
            },
          })
        }
      >
        {inUse ? (
          <>
            <p>Remove it from the Share panel of each file first:</p>
            <ul className={`${styles.files} ${styles.dialogFiles}`}>
              {group.usedBy.map((node) => (
                <li key={node.id}>
                  <UsedByLink node={node} onClick={() => setOpen(false)} />
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>No file uses it, so nobody loses access to anything.</p>
        )}
      </ConfirmDialog>
    </>
  );
}

function MembersPanel({ group }: { group: Group }) {
  const me = useQuery(meQuery);
  const patch = usePatchGroup(group.id);
  const inputId = useId();
  const owner = me.data?.email.toLowerCase();

  const failure = patch.error ? `Members did not save: ${messageOf(patch.error)}` : null;

  return (
    <section className={`${ui.card} ${filePage.panel}`} aria-labelledby={`${inputId}-heading`}>
      <div className={filePage.panelHead}>
        <h2 id={`${inputId}-heading`} className={filePage.panelTitle}>
          People
        </h2>
        <span className={filePage.peopleCount} data-testid="member-count">
          {formatCount(group.members.length, "person", "people")}
        </span>
      </div>

      <div className={styles.addBlock}>
        <label className={filePage.fieldLabel} htmlFor={inputId}>
          Add people
        </label>
        <EmailAddForm
          inputId={inputId}
          existing={group.members}
          owner={owner}
          placeholder="alice@example.com"
          failure={failure}
          onAdd={(fresh) => patch.mutate({ members: [...group.members, ...fresh] })}
        />
      </div>

      {group.members.length > 0 ? (
        <ul className={`${filePage.personList} ${styles.members}`}>
          {group.members.map((email) => (
            <PersonRow
              key={email}
              seed={email}
              title={email}
              detail={
                group.usedBy.length > 0
                  ? `Sees ${formatCount(group.usedBy.length, "file", "files")}`
                  : "Member"
              }
              removeLabel={`Remove ${email}`}
              onRemove={() =>
                patch.mutate({ members: group.members.filter((member) => member !== email) })
              }
            />
          ))}
        </ul>
      ) : (
        <p className={`${filePage.peopleEmpty} ${styles.membersEmpty}`}>
          <span className={filePage.emptyMark}>
            <Icon name="people" />
          </span>
          Nobody is in this group yet. Paste a list of addresses to add everyone at once.
        </p>
      )}
    </section>
  );
}

function SharedPanel({ group }: { group: Group }) {
  const headingId = useId();
  return (
    <section className={`${ui.card} ${filePage.panel}`} aria-labelledby={headingId}>
      <div className={filePage.panelHead}>
        <h2 id={headingId} className={filePage.panelTitle}>
          Files
        </h2>
      </div>
      {group.usedBy.length > 0 ? (
        <ul className={styles.files}>
          {group.usedBy.map((node) => (
            <li key={node.id}>
              <UsedByLink node={node} />
            </li>
          ))}
        </ul>
      ) : (
        <p className={`${filePage.peopleEmpty} ${styles.filesEmpty}`}>
          <span className={filePage.emptyMark}>
            <Icon name="files" />
          </span>
          Not on any file. Add it from a file’s Share panel.
        </p>
      )}
    </section>
  );
}

function UsedByLink({ node, onClick }: { node: Group["usedBy"][number]; onClick?: () => void }) {
  const folder = node.kind === "folder";
  return (
    <Link
      to={folder ? "/folders/$id" : "/files/$id"}
      params={{ id: node.id }}
      className={styles.fileLink}
      onClick={onClick}
    >
      {folder ? <FolderBadge /> : <FileBadge contentType={node.contentType} name={node.name} />}
      <span className={styles.fileName}>{node.name}</span>
      <Icon name="chevron" className={styles.chevron} />
    </Link>
  );
}

function GroupPending() {
  return (
    <div className={`${filePage.page} ${filePage.pending}`} aria-busy="true">
      <BackLink />
      <div className={filePage.pendingHeader}>
        <span className={`${ui.skeleton} ${filePage.pendingSticker}`} />
        <span className={`${ui.skeleton} ${filePage.pendingTitle}`} />
        <span className={`${ui.skeleton} ${filePage.pendingMeta}`} />
      </div>
      <div className={filePage.panels}>
        <div className={`${ui.card} ${filePage.panel}`}>
          <span className={`${ui.skeleton} ${filePage.pendingTitleBar}`} />
          <span className={`${ui.skeleton} ${filePage.pendingField}`} />
        </div>
        <div className={`${ui.card} ${filePage.panel}`}>
          <span className={`${ui.skeleton} ${filePage.pendingTitleBar}`} />
        </div>
      </div>
    </div>
  );
}

function GroupError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
    return (
      <StatePanel
        icon="people"
        tone="info"
        title="No group here"
        detail="The group was deleted or the address is wrong."
      >
        <Link to="/groups" className={ui.button}>
          All groups
        </Link>
      </StatePanel>
    );
  }
  return (
    <StatePanel icon="alert" title="This group did not load" detail={messageOf(error)}>
      <Link to="/groups" className={ui.button}>
        All groups
      </Link>
      <button
        type="button"
        className={ui.button}
        data-variant="primary"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
